// Skills.js - Enhanced with comprehensive skills and icons
import { motion } from 'framer-motion';
import './Skills.css';

// Import icons
import { 
  FaJs, FaPhp, FaJava, FaDatabase, FaHtml5, FaCss3Alt, 
  FaBootstrap, FaReact, FaNodeJs, FaGitAlt, FaGithub, 
  FaCode, FaServer, FaProjectDiagram, FaUsers, FaFileAlt,
  FaChartLine, FaTools, FaLock, FaExchangeAlt,
  FaMobileAlt, FaPaintBrush, FaWrench, FaPlug,
  FaCookieBite, FaUserClock, FaUserShield
} from 'react-icons/fa';
import { 
  SiMysql, SiJquery, SiTailwindcss, SiPostman, 
  SiJson, SiXml, SiChartdotjs, SiFramer,
  SiXampp, SiPhpmyadmin, SiGooglechrome
} from 'react-icons/si';
import { 
  MdApi, MdStorage, MdSecurity, 
  MdCookie, MdAccessTime, MdVerifiedUser,
  MdOutlineSession 
} from 'react-icons/md';

const skillGroups = [
  {
    title: 'Languages',
    icon: <FaCode />,
    items: [
      { name: 'JavaScript', icon: <FaJs style={{ color: '#F7DF1E' }} /> },
      { name: 'PHP', icon: <FaPhp style={{ color: '#777BB4' }} /> },
      { name: 'Java', icon: <FaJava style={{ color: '#007396' }} /> },
      { name: 'SQL', icon: <FaDatabase style={{ color: '#336791' }} /> },
      { name: 'HTML5', icon: <FaHtml5 style={{ color: '#E34F26' }} /> },
      { name: 'CSS3', icon: <FaCss3Alt style={{ color: '#1572B6' }} /> }
    ]
  },
  {
    title: 'Frontend Development',
    icon: <FaMobileAlt />,
    items: [
      { name: 'Bootstrap', icon: <FaBootstrap style={{ color: '#7952B3' }} /> },
      { name: 'Tailwind CSS', icon: <SiTailwindcss style={{ color: '#06B6D4' }} /> },
      { name: 'jQuery', icon: <SiJquery style={{ color: '#0769AD' }} /> },
      { name: 'Responsive Design', icon: <FaMobileAlt style={{ color: '#667EEA' }} /> },
      { name: 'AJAX', icon: <FaExchangeAlt style={{ color: '#FF6B6B' }} /> },
      { name: 'DOM Manipulation', icon: <FaCode style={{ color: '#61DAFB' }} /> }
    ]
  },
  {
    title: 'Backend Development',
    icon: <FaServer />,
    items: [
      { name: 'PHP', icon: <FaPhp style={{ color: '#777BB4' }} /> },
      { name: 'MySQL', icon: <SiMysql style={{ color: '#4479A1' }} /> },
      { name: 'RESTful API', icon: <MdApi style={{ color: '#FF6B6B' }} /> },
      { name: 'Server-Side Scripting', icon: <FaCode style={{ color: '#68217A' }} /> },
      { name: 'Database Management', icon: <FaDatabase style={{ color: '#336791' }} /> }
    ]
  },
  {
    title: 'Frameworks & Libraries',
    icon: <FaReact />,
    items: [
      { name: 'Bootstrap 5', icon: <FaBootstrap style={{ color: '#7952B3' }} /> },
      { name: 'jQuery', icon: <SiJquery style={{ color: '#0769AD' }} /> },
      { name: 'Font Awesome', icon: <FaPaintBrush style={{ color: '#339AF0' }} /> },
      { name: 'Chart.js', icon: <SiChartdotjs style={{ color: '#FF6384' }} /> },
      { name: 'Framer Motion', icon: <SiFramer style={{ color: '#0055FF' }} /> }
    ]
  },
  {
    title: 'Database Technologies',
    icon: <MdStorage />,
    items: [
      { name: 'MySQL', icon: <SiMysql style={{ color: '#4479A1' }} /> },
      { name: 'phpMyAdmin', icon: <SiPhpmyadmin style={{ color: '#6C78AF' }} /> },
      { name: 'Database Design', icon: <FaProjectDiagram style={{ color: '#FF9800' }} /> },
      { name: 'Query Optimization', icon: <FaChartLine style={{ color: '#4CAF50' }} /> },
      { name: 'Normalization', icon: <FaDatabase style={{ color: '#336791' }} /> }
    ]
  },
  {
    title: 'Developer Tools',
    icon: <FaWrench />,
    items: [
      { name: 'Git', icon: <FaGitAlt style={{ color: '#F05032' }} /> },
      { name: 'GitHub', icon: <FaGithub style={{ color: '#181717' }} /> },
      { name: 'VS Code', icon: <FaCode style={{ color: '#007ACC' }} /> },
      { name: 'XAMPP', icon: <SiXampp style={{ color: '#FB7A24' }} /> },
      { name: 'Chrome DevTools', icon: <SiGooglechrome style={{ color: '#4285F4' }} /> },
      { name: 'Postman', icon: <SiPostman style={{ color: '#FF6C37' }} /> }
    ]
  },
  {
    title: 'Web Technologies',
    icon: <FaPlug />,
    items: [
      { name: 'AJAX', icon: <FaExchangeAlt style={{ color: '#FF6B6B' }} /> },
      { name: 'JSON', icon: <SiJson style={{ color: '#000000' }} /> },
      { name: 'XML', icon: <SiXml style={{ color: '#0060AC' }} /> },
      { name: 'Sessions', icon: <MdAccessTime style={{ color: '#9C27B0' }} /> },
      { name: 'Cookies', icon: <FaCookieBite style={{ color: '#D4A373' }} /> },
      { name: 'Authentication', icon: <MdVerifiedUser style={{ color: '#4CAF50' }} /> },
      { name: 'Authorization', icon: <FaUserShield style={{ color: '#2196F3' }} /> }
    ]
  },
  {
    title: 'Professional Skills',
    icon: <FaUsers />,
    items: [
      { name: 'Project Management', icon: <FaProjectDiagram style={{ color: '#FF9800' }} /> },
      { name: 'Client Communication', icon: <FaUsers style={{ color: '#2196F3' }} /> },
      { name: 'Requirements Gathering', icon: <FaFileAlt style={{ color: '#607D8B' }} /> },
      { name: 'Documentation', icon: <FaFileAlt style={{ color: '#795548' }} /> }
    ]
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.03 },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

function Skills() {
  return (
    <section id="skills" className="skills section">
      <motion.h2
        className="section-title"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Technical Expertise
      </motion.h2>
      
      <motion.p
        className="section-subtitle"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Comprehensive skill set in web development and software engineering
      </motion.p>

      <div className="skills__grid">
        {skillGroups.map((group, gi) => (
          <motion.div
            key={group.title}
            className="skills__group"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 * gi }}
          >
            <div className="skills__group-header">
              <span className="skills__group-icon">{group.icon}</span>
              <h3 className="skills__group-title">{group.title}</h3>
            </div>
            <motion.ul
              className="skills__list"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {group.items.map((skill) => (
                <motion.li key={skill.name} className="skills__tag" variants={item}>
                  <span className="skills__tag-icon">{skill.icon}</span>
                  {skill.name}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
  
export default Skills;